// filepath: c:\Users\tomjo\Documents\Personal\Crypto\github-visstudio-projects\trade-dashboard-project\trade-dashboard-monorepo\frontend\src\utils\symbolUtils.ts
/**
 * MIGRATED TO COINGECKO ASSETS SYSTEM
 * 
 * This file has been updated to use the new CoinGecko-powered local asset system
 * which provides better reliability, quality, and fallback strategies.
 * 
 * Legacy function is preserved for compatibility but now uses the enhanced system.
 */

import { getSymbolAssetUrl, normalizeSymbol } from './coinGeckoAssets';

/**
 * Constructs the URL for a cryptocurrency symbol's logo.
 * 
 * ENHANCED: Now uses CoinGecko-powered local assets with intelligent fallbacks:
 * 1. Local optimized assets (WebP/PNG)
 * 2. CoinGecko CDN (high quality)
 * 3. Generated SVG fallback
 * 4. Legacy external URL (fallback)
 * 
 * @param symbol The trading symbol (e.g., "BTCUSDT").
 * @returns Promise<string> The URL string for the logo.
 */
export const getSymbolLogoUrl = async (symbol: string): Promise<string> => {
  try {
    const assetResponse = await getSymbolAssetUrl({
      symbol,
      size: 'small',
      format: 'png',
      fallback: true
    });
    
    return assetResponse.url;
  } catch (error) {
    console.warn(`Failed to get enhanced asset URL for ${symbol}, using legacy fallback:`, error);
    
    // Final fallback to legacy system
    const baseSymbol = normalizeSymbol(symbol);
    return `https://static-app.bb-os.com/icon/${baseSymbol}.png`;
  }
};

/**
 * LEGACY SYNCHRONOUS VERSION (DEPRECATED)
 * 
 * Constructs the URL for a cryptocurrency symbol's logo based on legacy pattern.
 * 
 * @deprecated Use getSymbolLogoUrl() instead for enhanced CoinGecko integration
 * @param symbol The trading symbol (e.g., "BTCUSDT").
 * @returns The URL string for the logo.
 */
export const getSymbolLogoUrlLegacy = (symbol: string): string => {
  const baseSymbol = normalizeSymbol(symbol);
  return `https://static-app.bb-os.com/icon/${baseSymbol}.png`;
};

/**
 * SIMPLE SYNCHRONOUS VERSION - Local Assets API
 * 
 * Constructs the URL for a cryptocurrency symbol's logo using our local assets API.
 * This provides immediate access to CoinGecko-powered local assets with fallbacks.
 * 
 * @param symbol The trading symbol (e.g., "BTCUSDT").
 * @param size Optional size parameter ('thumb', 'small', 'large'). Defaults to 'small'.
 * @returns The URL string for the logo from our local assets API.
 */
export const getSymbolLogoUrlSync = (symbol: string, size: 'thumb' | 'small' | 'large' = 'small'): string => {
  const baseSymbol = normalizeSymbol(symbol);
  return `/api/symbol-assets/symbols/${baseSymbol}/logo?size=${size}`;
};

/**
 * Get multiple symbol logo URLs efficiently with batching and prioritization
 * 
 * @param symbols Array of trading symbols
 * @param options Configuration options
 * @returns Promise<Array<{symbol: string, url: string, source: string}>>
 */
export const getMultipleSymbolLogoUrls = async (
  symbols: string[],
  options: {
    size?: 'thumb' | 'small' | 'large';
    format?: 'webp' | 'png' | 'svg';
    fallback?: boolean;
  } = {}
): Promise<Array<{ symbol: string; url: string; source: string }>> => {
  const { size = 'small', format = 'png', fallback = true } = options;
  
  try {
    const requests = symbols.map(symbol => ({
      symbol,
      size,
      format,
      fallback
    }));
    
    const responses = await Promise.all(
      requests.map(async request => {
        const response = await getSymbolAssetUrl(request);
        return {
          symbol: request.symbol,
          url: response.url,
          source: response.source
        };
      })
    );
    
    return responses;
  } catch (error) {
    console.warn('Failed to get multiple symbol URLs, using legacy fallbacks:', error);
    
    // Fallback to legacy system for all
    return symbols.map(symbol => ({
      symbol,
      url: getSymbolLogoUrlLegacy(symbol),
      source: 'external-fallback'
    }));
  }
};

/**
 * Preload symbol assets for better performance
 * 
 * @param symbols Array of symbols to preload
 */
export const preloadSymbolAssets = async (symbols: string[]): Promise<void> => {
  try {
    // Preload in background without blocking
    getMultipleSymbolLogoUrls(symbols, { size: 'small' }).catch(error => {
      console.warn('Failed to preload symbol assets:', error);
    });
  } catch (error) {
    console.warn('Failed to initiate symbol asset preloading:', error);
  }
};
